<template>
    <div class="flex flex-column justify-content-center align-items-center w-full h-full">
        <span class="H6-Medium pb-6">
            {{ isEdit ? "Edit Note" : "Create a New Note" }}
        </span>

        <form @submit.prevent="handleSubmit" class="form">
            <div class="grid w-full align-items-center justify-content-center">

                <div class="col-12 md:col-6 flex flex-column gap-2 mb-5 relative">
                    <label for="title" class="Label align-self-start">Note Title</label>
                    <InputText id="title" v-model="title" placeholder="Enter title here"
                        size="small" variant="filled" class="w-full" />
                    <small v-if="errors.title" class="error-text">{{ errors.title }}</small>
                </div>

                <div class="col-12 md:col-6 flex flex-column gap-2 mb-5 relative">
                    <label for="subject" class="Label align-self-start">Subject</label>
                    <InputText id="subject" v-model="subject" placeholder="Enter subject here"
                        size="small" variant="filled" class="w-full" />
                    <small v-if="errors.subject" class="error-text">{{ errors.subject }}</small>
                </div>

                <div class="col-12 flex flex-column gap-2 mb-5 relative">
                    <label for="note" class="Label align-self-start">Note</label>
                    <Editor id="note" v-model="note" editorStyle="height: 280px" />
                    <small v-if="errors.note" class="error-text">{{ errors.note }}</small>
                </div>

                <div class="col-12 flex justify-content-start">
                    <Button type="submit"
                        :label="isEdit ? 'Update Note' : 'Create Note'"
                        class="orange-button-small" />
                </div>
            </div>
        </form>
    </div>
</template>

<script setup>
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Editor from "primevue/editor";
import { notifyError, notifySuccess, notifyWarn } from "@/utils/notification";
import { createNote, updateNote } from "@/api/student";
import { ref, defineProps, computed, watch, defineEmits } from "vue";

const props = defineProps({
    note: {
        type: Object,
        default: null
    }
});
const emit = defineEmits(['refresh-notes']);


const title = ref("");
const subject = ref("");
const note = ref("");
const errors = ref({});

const isEdit = computed(() => !!props.note);

const validateForm = () => {
    errors.value = {};

    if (!title.value) errors.value.title = "Note Title is required";
    if (!subject.value) errors.value.subject = "Subject is required";
    // Check for empty editor state
    if (!note.value || note.value === '<p><br></p>') {
        errors.value.note = "Note content is required";
    }

    return Object.keys(errors.value).length === 0;
};

// --- WATCHERS ---
// Watchers to clear errors as user types
watch(title, (val) => { if (val) errors.value.title = null; });
watch(subject, (val) => { if (val) errors.value.subject = null; });
watch(note, (val) => {
    if (val && val !== '<p><br></p>') errors.value.note = null;
});

// Watcher to populate form when editing
watch(
  () => props.note,
  (newNote) => {
    if (newNote) {
      title.value = newNote.title || "";
      subject.value = newNote.subject || "";
      note.value = newNote.note || "";
    } else {
      title.value = "";
      subject.value = "";
      note.value = "";
    }
    errors.value = {}; // Clear errors when note changes
  },
  { immediate: true }
);

// --- SUBMISSION ---
async function handleSubmit() {
    if (!validateForm()) {
        notifyError("Error", "Please fill all required fields.");
        return;
    }

    const payload = {
        title: title.value,
        subject: subject.value,
        note: note.value,
    };

    try {
        if (isEdit.value) {
            await updateNote(payload, props.note._id);
            notifySuccess("Success", "Note updated successfully");
        } else {
            await createNote(payload);
            notifySuccess("Success", "Note created successfully");
        }
    } catch (error) {
        const responseData = error?.response?.data;
        const errorMsg = responseData?.message || "An error occurred. Please try again.";

        if (error?.response?.status === 403) {
            notifyWarn("Limit Reached", errorMsg);
        } else {
            notifyError(responseData?.status || "Error", errorMsg);
        }
    } finally {
        if (!isEdit.value) {
            title.value = "";
            subject.value = "";
            note.value = "";
        }
        emit('refresh-notes');
    }
}
</script>

<style scoped>
.form { width: 100%; }

.error-text {
    color: #dc2626;
    font-size: 0.85rem;
    position: absolute;
    top: 100%;
    margin-top: 0.5rem;
    left: 0;
    padding-left: 0.5rem;
}
</style>